import React from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Typography,
  Icon,
  InputAdornment,
  alpha,
} from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import styled from "@emotion/styled";
import { Theme } from "@mui/material/styles";

const StyledTelInput = styled(MuiTelInput)<{ theme?: Theme }>`
  & .MuiOutlinedInput-root {
    border-radius: 4px;
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme?.palette.primary.main};
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme?.palette.primary.main};
      border-width: 2px;
    }
  }
  & .MuiInputLabel-root {
    color: ${({ theme }) => theme?.palette.text.secondary};
  }
`;

interface PersonalInformationProps {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  loading: boolean;
  errors: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
  };
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
}

export const StaffBuilderPersonalInformation: React.FC<
  PersonalInformationProps
> = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  loading,
  errors,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPhoneNumberChange,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: "1px solid",
        borderColor: alpha("#0891B2", 0.1),
        borderRadius: 1,
        height: "100%",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            color: "#0891B2",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontFamily: "Montserrat",
          }}
        >
          <Icon>person</Icon>
          Personal Information
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: "Montserrat" }}
        >
          Enter the staff member's basic details
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="First Name"
            placeholder="Enter first name"
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            error={!!errors.first_name}
            helperText={errors.first_name}
            required
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: "#0891B2" }}>badge</Icon>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0891B2",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0891B2",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#0891B2",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Last Name"
            placeholder="Enter last name"
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            error={!!errors.last_name}
            helperText={errors.last_name}
            required
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: "#0891B2" }}>badge</Icon>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0891B2",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0891B2",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#0891B2",
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email Address"
            placeholder="Enter work email address"
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            error={!!errors.email}
            helperText={errors.email || "This will be used for login"}
            required
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: "#0891B2" }}>email</Icon>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0891B2",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0891B2",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#0891B2",
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <StyledTelInput
            value={phoneNumber}
            onChange={onPhoneNumberChange}
            defaultCountry="BH"
            onlyCountries={["BH", "SA", "AE", "KW", "OM", "QA"]}
            langOfCountryName="en"
            fullWidth
            label="Phone Number"
            error={!!errors.phone_number}
            helperText={errors.phone_number}
            disabled={loading}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0891B2",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0891B2",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#0891B2",
              },
              "& .MuiTelInput-Flag": {
                borderRadius: 1,
                mr: 1,
              },
              "& .MuiTelInput-CountrySelect": {
                "& .MuiSelect-select": {
                  pl: 2,
                },
              },
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
